/**
 * Every settings section, grouped by the area that owns it (DEV-132,
 * docs/plans/settings-architecture.md §3). The menu, the addresses and the
 * redirects from the old one-tab-per-page addresses are all derived from this
 * list, so a new section is one entry in the registry plus its page.
 *
 * The sections and who may read and change each one are the shared registry
 * in `@norvix/utils` (settings-access, DEV-140), the same lists the API
 * enforces. A role sees only the sections it may read; on a section it may
 * read but not change, the page shows no save bar and no edit controls.
 */

import {
  SETTINGS_GROUPS as SHARED_GROUPS,
  SETTINGS_SECTIONS as SHARED_SECTIONS,
  maySettings,
  settingsRestorePolicy,
  settingsSectionKey,
  type SettingsGroupDefinition,
  type SettingsSectionDefinition,
} from '@norvix/utils';

export type SettingsGroup = SettingsGroupDefinition;
export type SettingsSection = SettingsSectionDefinition;

/** In menu order. A group with no section visible to the user is not shown. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = SHARED_GROUPS;
export const SETTINGS_SECTIONS: readonly SettingsSection[] = SHARED_SECTIONS;

export function sectionPath(section: Pick<SettingsSection, 'group' | 'id'>): string {
  return `/settings/${section.group}/${section.id}`;
}

/** The role may open the section and read it. */
export function canReadSection(role: string | null | undefined, section: Pick<SettingsSection, 'group' | 'id'>): boolean {
  return maySettings(role, settingsSectionKey(section), 'read');
}

/** The role may change the section: the page shows its save bar and edit controls. */
export function canWriteSection(role: string | null | undefined, section: Pick<SettingsSection, 'group' | 'id'>): boolean {
  return maySettings(role, settingsSectionKey(section), 'write');
}

/** DEV-142: why a section offers no restore, for its History panel; undefined where it does. */
export function noRestoreNote(section: Pick<SettingsSection, 'group' | 'id'>): string | undefined {
  const policy = settingsRestorePolicy(settingsSectionKey(section));
  return policy.offered ? undefined : policy.why;
}

export function visibleSections(role: string | null | undefined): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => canReadSection(role, section));
}

/** The groups to show, each with its visible sections, in menu order. */
export function settingsMenu(role: string | null | undefined): Array<SettingsGroup & { sections: SettingsSection[] }> {
  const sections = visibleSections(role);
  return SETTINGS_GROUPS
    .map((group) => ({ ...group, sections: sections.filter((s) => s.group === group.id) }))
    .filter((group) => group.sections.length > 0);
}

export function findSection(group: string | undefined, id: string | undefined): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((section) => section.group === group && section.id === id);
}

/** Where an old `/settings/<tab>` address lives now; undefined when it never existed. */
export function legacySectionPath(legacyId: string | undefined): string | undefined {
  const section = SETTINGS_SECTIONS.find((s) => s.legacyId !== undefined && s.legacyId === legacyId);
  return section === undefined ? undefined : sectionPath(section);
}

/** A group's own address (`/settings/<group>`, e.g. from the breadcrumb) opens its first section the role may see. */
export function groupSectionPath(role: string | null | undefined, groupId: string | undefined): string | undefined {
  const first = visibleSections(role).find((s) => s.group === groupId);
  return first === undefined ? undefined : sectionPath(first);
}

/** Breadcrumb names for every group and section address segment. */
export const SETTINGS_SEGMENT_LABELS: Readonly<Record<string, string>> = Object.fromEntries([
  ...SETTINGS_GROUPS.map((g): [string, string] => [g.id, g.label]),
  ...SETTINGS_SECTIONS.map((s): [string, string] => [s.id, s.label]),
]);

/**
 * Where `/settings` opens: the first section of the role's usual area, then
 * the first it may change, then the first it may read; undefined when it sees none.
 */
export function defaultSectionPath(role: string | null | undefined): string | undefined {
  const visible = visibleSections(role);
  const usual = [findSection('connections', 'providers'), findSection('compliance', 'transaction-monitoring')]
    .find((s) => s !== undefined && canWriteSection(role, s));
  const target = usual ?? visible.find((s) => canWriteSection(role, s)) ?? visible[0];
  return target === undefined ? undefined : sectionPath(target);
}
